import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import './SideNavbar.scss'
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { SidebarOption } from '../../../app/utils/constants/SidebarOptions';
import { checkPermission, hasAttendanceModuleAccess, canReadLeaveManagement, canPostTeamPraise, canReadProjectDocumentation, hasAdminAccess, HR_ROLE_ID, permissionType, canManageSops, canReadSops, canManageOffboardingDocuments, canReadOffboardingDocuments, canViewOwnSalarySlipsNav, canAccessProjectCredentialsPage, canReadTimeTracker, canReadTimeTrackerAdmin } from '../../../app/utils/common/Functions';
import { canManageLeaveBalances } from '../../../app/utils/leave/leaveManagementTabs';
import { useDispatch, useSelector } from 'react-redux';
import { clearToken } from '../../../Redux/Slices/authSlice';
import ConfirmModal from '../../atoms/ConfirmModal/ConfirmModal';
import { setProjectSliceToInitial } from '../../../Redux/Slices/projectSlice';
import { image } from '../../../app/utils/common/image';
import { protectedApi } from '../../../app/utils/common/protectedApi';
import { APIS, DAILY_HOURS_ACCESS_PENDING_REFRESH_EVENT, LEAVE_APPROVE_PENDING_REFRESH_EVENT } from '../../../app/utils/constants/apiConstant';

const PROJECT_DOC_PENDING_EVENT = 'project-documentation-pending-count';

const isNavItemActive = (pathname, navigatePath, key) => {
    if (key === 'logout' || !navigatePath || navigatePath === 'logout') return false;
    if (pathname === navigatePath) return true;
    return pathname.startsWith(`${navigatePath}/`);
};

const getNavItemClassSuffix = (key) => {
    if (key === "project_documentation") return " nav-item--project-docs";
    if (key === "leave_approve") return " nav-item--leave-approve";
    if (key === "daily_hours") return " nav-item--daily-hours";
    return "";
};

const SideNavbar = ({ collapsed, drawerOpen = false, isMobile = false, onCloseMobile }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth.auth);
    const [logoutModalOpen, setLogoutModalOpen] = useState(false);
    const [projectDocPendingCount, setProjectDocPendingCount] = useState(0);
    const [leaveApprovePendingCount, setLeaveApprovePendingCount] = useState(0);
    const [dailyHoursPendingCount, setDailyHoursPendingCount] = useState(0);
    const [hasAssignedSops, setHasAssignedSops] = useState(false);
    const [sopsNavChecked, setSopsNavChecked] = useState(false);
    const [hasAssignedOffboardingDocs, setHasAssignedOffboardingDocs] = useState(false);
    const [offboardingNavChecked, setOffboardingNavChecked] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    const [collapsedPanelGroupKey, setCollapsedPanelGroupKey] = useState(null);
    const [collapsedPanelTop, setCollapsedPanelTop] = useState(0);
    const sideNavRef = useRef(null);
    const collapsedPanelRef = useRef(null);
    const showCollapsedNav = !isMobile && collapsed;
    const showCollapsedTooltips = showCollapsedNav && !collapsedPanelGroupKey;

    const canManageSopsNav = Boolean(user && canManageSops(user));
    const shouldCheckAssignedSops = Boolean(user && canReadSops() && !canManageSopsNav);
    const canManageOffboardingNav = Boolean(user && canManageOffboardingDocuments(user));
    const shouldCheckAssignedOffboarding =
        Boolean(user && canReadOffboardingDocuments() && !canManageOffboardingNav);

    const fetchAssignedSopsVisibility = useCallback(async () => {
        if (!shouldCheckAssignedSops) {
            setHasAssignedSops(false);
            setSopsNavChecked(true);
            return;
        }
        try {
            const response = await protectedApi({
                url: APIS.SOPS,
                method: "GET",
            });
            const sops = response?.data?.data?.sops;
            setHasAssignedSops(Array.isArray(sops) && sops.length > 0);
        } catch {
            setHasAssignedSops(false);
        } finally {
            setSopsNavChecked(true);
        }
    }, [shouldCheckAssignedSops]);

    const fetchAssignedOffboardingVisibility = useCallback(async () => {
        if (!shouldCheckAssignedOffboarding) {
            setHasAssignedOffboardingDocs(false);
            setOffboardingNavChecked(true);
            return;
        }
        try {
            const response = await protectedApi({
                url: APIS.OFFBOARDING_DOCUMENTS,
                method: "GET",
            });
            const assignments = response?.data?.data?.assignments;
            setHasAssignedOffboardingDocs(Array.isArray(assignments) && assignments.length > 0);
        } catch {
            setHasAssignedOffboardingDocs(false);
        } finally {
            setOffboardingNavChecked(true);
        }
    }, [shouldCheckAssignedOffboarding]);

    const canFetchProjectDocPending = Boolean(
        user && (hasAdminAccess(user) || checkPermission(permissionType.PROJECT_DOCUMENTATION, "update"))
    );

    const canFetchLeaveApprovePending = Boolean(
        user && checkPermission(permissionType.LEAVE_APPROVE, "read")
    );
    const canFetchDailyHoursPending = Boolean(
        user && (hasAdminAccess(user) || Number(user?.roleId) === HR_ROLE_ID)
    );

    const fetchProjectDocPendingCount = useCallback(async () => {
        if (!canFetchProjectDocPending) {
            setProjectDocPendingCount(0);
            return;
        }
        try {
            const response = await protectedApi({
                url: APIS.PROJECT_DOCUMENTATION_EDIT_REQUESTS_PENDING,
                method: "GET",
            });
            const list = Array.isArray(response?.data?.data) ? response.data.data : [];
            setProjectDocPendingCount(list.length);
        } catch {
            setProjectDocPendingCount(0);
        }
    }, [canFetchProjectDocPending]);

    const fetchLeaveApprovePendingCount = useCallback(async () => {
        if (!canFetchLeaveApprovePending) {
            setLeaveApprovePendingCount(0);
            return;
        }
        try {
            const response = await protectedApi({
                url: `${APIS.GET_ALL_LEAVES}?page=1&limit=1&attention=1`,
                method: "GET",
            });
            const total = response?.data?.data?.pagination?.totalData;
            setLeaveApprovePendingCount(typeof total === "number" ? total : 0);
        } catch {
            setLeaveApprovePendingCount(0);
        }
    }, [canFetchLeaveApprovePending]);

    const fetchDailyHoursPendingCount = useCallback(async () => {
        if (!canFetchDailyHoursPending) {
            setDailyHoursPendingCount(0);
            return;
        }
        try {
            const response = await protectedApi({
                url: APIS.TASK_MONTHLY_ACCESS_PENDING_REQUESTS,
                method: "GET",
            });
            const list = Array.isArray(response?.data?.data) ? response.data.data : [];
            setDailyHoursPendingCount(list.length);
        } catch {
            setDailyHoursPendingCount(0);
        }
    }, [canFetchDailyHoursPending]);

    useEffect(() => {
        fetchAssignedSopsVisibility();
    }, [fetchAssignedSopsVisibility]);

    useEffect(() => {
        fetchAssignedOffboardingVisibility();
    }, [fetchAssignedOffboardingVisibility]);

    useEffect(() => {
        if (!shouldCheckAssignedSops) return undefined;
        const onFocus = () => fetchAssignedSopsVisibility();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [shouldCheckAssignedSops, fetchAssignedSopsVisibility]);

    useEffect(() => {
        if (!shouldCheckAssignedOffboarding) return undefined;
        const onFocus = () => fetchAssignedOffboardingVisibility();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [shouldCheckAssignedOffboarding, fetchAssignedOffboardingVisibility]);

    useEffect(() => {
        fetchProjectDocPendingCount();
    }, [fetchProjectDocPendingCount]);

    useEffect(() => {
        fetchLeaveApprovePendingCount();
    }, [fetchLeaveApprovePendingCount]);
    useEffect(() => {
        fetchDailyHoursPendingCount();
    }, [fetchDailyHoursPendingCount]);

    useEffect(() => {
        const onPendingCount = (e) => {
            if (typeof e.detail?.count === "number") {
                setProjectDocPendingCount(e.detail.count);
            }
        };
        window.addEventListener(PROJECT_DOC_PENDING_EVENT, onPendingCount);
        return () => window.removeEventListener(PROJECT_DOC_PENDING_EVENT, onPendingCount);
    }, []);

    useEffect(() => {
        const onLeaveApproveRefresh = () => {
            fetchLeaveApprovePendingCount();
        };
        window.addEventListener(LEAVE_APPROVE_PENDING_REFRESH_EVENT, onLeaveApproveRefresh);
        return () => window.removeEventListener(LEAVE_APPROVE_PENDING_REFRESH_EVENT, onLeaveApproveRefresh);
    }, [fetchLeaveApprovePendingCount]);
    useEffect(() => {
        const onDailyHoursRefresh = () => {
            fetchDailyHoursPendingCount();
        };
        window.addEventListener(DAILY_HOURS_ACCESS_PENDING_REFRESH_EVENT, onDailyHoursRefresh);
        return () => window.removeEventListener(DAILY_HOURS_ACCESS_PENDING_REFRESH_EVENT, onDailyHoursRefresh);
    }, [fetchDailyHoursPendingCount]);

    useEffect(() => {
        if (!canFetchLeaveApprovePending) return undefined;
        const id = setInterval(() => fetchLeaveApprovePendingCount(), 120000);
        return () => clearInterval(id);
    }, [canFetchLeaveApprovePending, fetchLeaveApprovePendingCount]);

    useEffect(() => {
        if (!canFetchProjectDocPending) return undefined;
        const id = setInterval(() => fetchProjectDocPendingCount(), 120000);
        return () => clearInterval(id);
    }, [canFetchProjectDocPending, fetchProjectDocPendingCount]);
    useEffect(() => {
        if (!canFetchDailyHoursPending) return undefined;
        const id = setInterval(() => fetchDailyHoursPendingCount(), 120000);
        return () => clearInterval(id);
    }, [canFetchDailyHoursPending, fetchDailyHoursPendingCount]);

    const handleNavigate = (route, key) => {
        if (key === 'logout') {
            setLogoutModalOpen(true);
        } else {
            navigate(route);
            setCollapsedPanelGroupKey(null);
            if (isMobile && onCloseMobile) {
                onCloseMobile();
            }
        }
    }

    const handleLogout = () => {
        dispatch(setProjectSliceToInitial());
        dispatch(clearToken());
        navigate('/login');
        setLogoutModalOpen(false);
    }

    const canViewNavItem = useCallback((data) => {
        if (data.isGroup && Array.isArray(data.children)) {
            return data.children.some((child) => canViewNavItem(child));
        }
        if (data.key === "dashboard" || data.key === "profile" || data.key === "logout") return true;
        if (data.key === "my_salary_slips") {
            return canViewOwnSalarySlipsNav(user);
        }
        if (hasAdminAccess(user)) {
            if (data.nonAdminOnly || data.adminHidden) return false;
            return true;
        }
        if (data.nonAdminOnly) {
            return !hasAdminAccess(user);
        }
        if (data.key === "attendance" || data.key === "attendance_history") {
            return hasAttendanceModuleAccess();
        }
        if (data.key === "task_filter") {
            return checkPermission(data.permission, "read") || checkPermission(data.permission, "read users");
        }
        if (data.key === "leave_request") {
            return canReadLeaveManagement();
        }
        if (data.key === "leave_balances" || data.hrOrAdminOnly) {
            return canManageLeaveBalances(user) && checkPermission(data.permission, "read");
        }
        if (data.key === "project_documentation") {
            return canReadProjectDocumentation();
        }
        if (data.key === "praise_message") {
            return canPostTeamPraise(user);
        }
        if (data.key === "sops") {
            if (!canReadSops()) return false;
            if (canManageSopsNav) return true;
            if (!sopsNavChecked) return false;
            return hasAssignedSops;
        }
        if (data.key === "offboarding_documents") {
            if (!canReadOffboardingDocuments()) return false;
            if (canManageOffboardingNav) return true;
            if (!offboardingNavChecked) return false;
            return hasAssignedOffboardingDocs;
        }
        if (data.key === "project_credentials") {
            return canAccessProjectCredentialsPage(user);
        }
        if (data.key === "time_tracker") {
            return canReadTimeTracker();
        }
        if (data.key === "time_tracker_admin") {
            return canReadTimeTrackerAdmin(user);
        }
        if (Array.isArray(data.anyPermissionChecks) && data.anyPermissionChecks.length) {
            return data.anyPermissionChecks.some(({ moduleId, keys }) =>
                (keys || ["read"]).some((key) => checkPermission(moduleId, key))
            );
        }
        if (Array.isArray(data.anyPermissions) && data.anyPermissions.length) {
            return data.anyPermissions.some((permissionId) => checkPermission(permissionId, "read"));
        }
        return checkPermission(data.permission, "read");
    }, [user, canManageSopsNav, sopsNavChecked, hasAssignedSops, canManageOffboardingNav, offboardingNavChecked, hasAssignedOffboardingDocs]);

    const getVisibleChildren = useCallback((group) => {
        if (!Array.isArray(group?.children)) return [];
        return group.children.filter((child) => canViewNavItem(child));
    }, [canViewNavItem]);

    const isGroupActive = useCallback((group) => {
        return getVisibleChildren(group).some((child) =>
            isNavItemActive(location.pathname, child.navigate, child.key)
        );
    }, [getVisibleChildren, location.pathname]);

    useEffect(() => {
        const activeGroups = SidebarOption
            .filter((item) => item.isGroup && isGroupActive(item))
            .map((item) => item.key);
        if (!activeGroups.length) return;
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            activeGroups.forEach((key) => next.add(key));
            return next;
        });
    }, [location.pathname, isGroupActive]);

    useEffect(() => {
        setCollapsedPanelGroupKey(null);
    }, [collapsed, drawerOpen, location.pathname]);

    useEffect(() => {
        if (!showCollapsedNav || !collapsedPanelGroupKey) return undefined;

        const handlePointerDown = (event) => {
            if (sideNavRef.current?.contains(event.target)) return;
            setCollapsedPanelGroupKey(null);
        };

        const handleEscape = (event) => {
            if (event.key === "Escape") setCollapsedPanelGroupKey(null);
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showCollapsedNav, collapsedPanelGroupKey]);

    const toggleGroup = (groupKey) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    };

    const getPendingCount = (key) => {
        if (key === "project_documentation") return projectDocPendingCount;
        if (key === "leave_approve") return leaveApprovePendingCount;
        if (key === "daily_hours") return dailyHoursPendingCount;
        return 0;
    };

    const getGroupPendingCount = (group) => {
        return getVisibleChildren(group).reduce((sum, child) => sum + getPendingCount(child.key), 0);
    };

    const collapsedPanelGroup = useMemo(() => {
        if (!collapsedPanelGroupKey) return null;
        return SidebarOption.find((item) => item.key === collapsedPanelGroupKey && item.isGroup) || null;
    }, [collapsedPanelGroupKey]);

    const collapsedPanelChildren = useMemo(() => {
        if (!collapsedPanelGroup) return [];
        return getVisibleChildren(collapsedPanelGroup);
    }, [collapsedPanelGroup, getVisibleChildren]);

    const updateCollapsedPanelPosition = useCallback((anchorEl) => {
        const sideNav = sideNavRef.current;
        if (!sideNav || !anchorEl) return;

        const navRect = sideNav.getBoundingClientRect();
        const anchorRect = anchorEl.getBoundingClientRect();
        let top = anchorRect.top - navRect.top;

        const panel = collapsedPanelRef.current;
        if (panel) {
            const panelHeight = panel.offsetHeight;
            const maxTop = Math.max(8, sideNav.clientHeight - panelHeight - 8);
            top = Math.min(Math.max(8, top), maxTop);
        }

        setCollapsedPanelTop(top);
    }, []);

    useLayoutEffect(() => {
        if (!showCollapsedNav || !collapsedPanelGroupKey) return;
        const trigger = sideNavRef.current?.querySelector(
            `[data-collapsed-group-trigger="${collapsedPanelGroupKey}"]`
        );
        if (trigger) updateCollapsedPanelPosition(trigger);
    }, [
        showCollapsedNav,
        collapsedPanelGroupKey,
        collapsedPanelChildren.length,
        updateCollapsedPanelPosition,
    ]);

    const renderPendingBadge = (key, showCollapsedDot = false) => {
        const count = getPendingCount(key);
        if (count <= 0) return null;
        if (showCollapsedDot) {
            return <span className="nav-item__pending-dot" aria-hidden />;
        }
        return (
            <span className={`${key === "project_documentation" ? "project-doc-nav-badge" : key === "leave_approve" ? "leave-approve-nav-badge" : "daily-hours-nav-badge"} shrink-0`}>
                {count > 99 ? "99+" : count}
            </span>
        );
    };

    const renderNavItem = (data, { isChild = false } = {}) => {
        const active = isNavItemActive(location.pathname, data.navigate, data.key);
        const pendingCount = getPendingCount(data.key);

        return (
            <div
                key={data.key}
                onClick={() => handleNavigate(data.navigate, data.key)}
                className={`mb-2 flex gap-2 cursor-pointer nav-item items-center${isChild ? " nav-item--child" : ""}${showCollapsedNav ? " nav-item--collapsed" : ""}${active ? " nav-item--active" : ""}${getNavItemClassSuffix(data.key)}`}
                data-tooltip-id={showCollapsedTooltips ? "collapsed-nav-tooltip" : undefined}
                data-tooltip-place="right"
                data-tooltip-content={showCollapsedTooltips ? data.text : ""}
                title={showCollapsedTooltips ? data.text : undefined}
            >
                {!isChild && (
                    <span className="nav-item__icon-wrap relative inline-flex shrink-0">
                        <img src={data.icon} alt="" aria-hidden="true" />
                        {showCollapsedNav && pendingCount > 0 ? renderPendingBadge(data.key, true) : null}
                    </span>
                )}
                {!showCollapsedNav && (
                    <>
                        {isChild ? <span className="nav-item__child-bullet" aria-hidden /> : null}
                        <span className="nav-item__label flex-1 min-w-0">{data.text}</span>
                        {renderPendingBadge(data.key)}
                    </>
                )}
            </div>
        );
    };

    const renderGroup = (group) => {
        const visibleChildren = getVisibleChildren(group);
        if (!visibleChildren.length) return null;

        const isExpanded = expandedGroups.has(group.key);
        const groupActive = isGroupActive(group);
        const groupPendingCount = getGroupPendingCount(group);
        const isPanelOpen = showCollapsedNav && collapsedPanelGroupKey === group.key;

        const handleGroupClick = (event) => {
            if (showCollapsedNav) {
                const isOpening = collapsedPanelGroupKey !== group.key;
                if (isOpening) {
                    updateCollapsedPanelPosition(event.currentTarget);
                    setCollapsedPanelGroupKey(group.key);
                } else {
                    setCollapsedPanelGroupKey(null);
                }
                return;
            }
            toggleGroup(group.key);
        };

        return (
            <div
                key={group.key}
                className={`nav-group${groupActive ? " nav-group--active" : ""}${isExpanded ? " nav-group--expanded" : ""}${isPanelOpen ? " nav-group--panel-open" : ""}`}
            >
                <div
                    onClick={handleGroupClick}
                    data-collapsed-group-trigger={showCollapsedNav ? group.key : undefined}
                    className={`mb-2 flex gap-2 cursor-pointer nav-item nav-item--group items-center${showCollapsedNav ? " nav-item--collapsed" : ""}${groupActive ? " nav-item--active" : ""}${isPanelOpen ? " nav-item--panel-open" : ""}`}
                    data-tooltip-id={showCollapsedTooltips ? "collapsed-nav-tooltip" : undefined}
                    data-tooltip-place="right"
                    data-tooltip-content={showCollapsedTooltips ? group.text : ""}
                    title={showCollapsedTooltips ? group.text : undefined}
                    aria-expanded={showCollapsedNav ? isPanelOpen : isExpanded}
                >
                    <span className="nav-item__icon-wrap relative inline-flex shrink-0">
                        <img src={group.icon} alt="" aria-hidden="true" />
                        {showCollapsedNav && groupPendingCount > 0 ? (
                            <span className="nav-item__pending-dot" aria-hidden />
                        ) : null}
                        {showCollapsedNav ? (
                            <span className="nav-item__group-indicator" aria-hidden />
                        ) : null}
                    </span>
                    {!showCollapsedNav && (
                        <>
                            <span className="nav-item__label flex-1 min-w-0">{group.text}</span>
                            {groupPendingCount > 0 ? (
                                <span className="project-doc-nav-badge shrink-0">
                                    {groupPendingCount > 99 ? "99+" : groupPendingCount}
                                </span>
                            ) : null}
                            <span className={`nav-group__chevron${isExpanded ? " nav-group__chevron--expanded" : ""}`} aria-hidden />
                        </>
                    )}
                </div>
                {!showCollapsedNav && isExpanded ? (
                    <div className="nav-group__children">
                        {visibleChildren.map((child) => renderNavItem(child, { isChild: true }))}
                    </div>
                ) : null}
            </div>
        );
    };

    const renderCollapsedPanel = () => {
        if (!showCollapsedNav || !collapsedPanelGroup || !collapsedPanelChildren.length) return null;

        return (
            <aside
                ref={collapsedPanelRef}
                className="side-nav-collapsed-panel"
                style={{ top: collapsedPanelTop }}
                aria-label={`${collapsedPanelGroup.text} menu`}
            >
                <header className="side-nav-collapsed-panel__header">
                    <div>
                        <p className="side-nav-collapsed-panel__eyebrow">Menu</p>
                        <h2 className="side-nav-collapsed-panel__title">{collapsedPanelGroup.text}</h2>
                    </div>
                    <button
                        type="button"
                        className="side-nav-collapsed-panel__close"
                        onClick={() => setCollapsedPanelGroupKey(null)}
                        aria-label="Close menu"
                    >
                        ×
                    </button>
                </header>
                <div className="side-nav-collapsed-panel__list" role="menu">
                    {collapsedPanelChildren.map((child) => {
                        const active = isNavItemActive(location.pathname, child.navigate, child.key);
                        const pendingCount = getPendingCount(child.key);
                        return (
                            <button
                                key={child.key}
                                type="button"
                                role="menuitem"
                                className={`side-nav-collapsed-panel__item${active ? " side-nav-collapsed-panel__item--active" : ""}${getNavItemClassSuffix(child.key)}`}
                                onClick={() => handleNavigate(child.navigate, child.key)}
                            >
                                <span className="side-nav-collapsed-panel__item-label">{child.text}</span>
                                {pendingCount > 0 ? (
                                    <span className="project-doc-nav-badge shrink-0">
                                        {pendingCount > 99 ? "99+" : pendingCount}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            </aside>
        );
    };

    const navItems = useMemo(() => {
        return SidebarOption.filter((data) => canViewNavItem(data));
    }, [canViewNavItem]);

    return (
        user &&
        <div
            ref={sideNavRef}
            className={`side-nav${showCollapsedNav ? " collapsed" : ""}${drawerOpen ? " side-nav--drawer-open" : ""}${collapsedPanelGroupKey ? " side-nav--panel-open" : ""}`}
            aria-hidden={isMobile ? !drawerOpen : undefined}
        >
            {showCollapsedNav && collapsedPanelGroupKey ? (
                <button
                    type="button"
                    className="side-nav-collapsed-backdrop"
                    aria-label="Close navigation menu"
                    onClick={() => setCollapsedPanelGroupKey(null)}
                />
            ) : null}
            <div className='navbar-logo'>
                {showCollapsedNav ? (
                    <div className="nav-brand-mark" aria-label="TCS">
                        <img src={image.LOGO} alt="" aria-hidden="true" className="nav-brand-mark__logo" />
                    </div>
                ) : (
                    <img src={image.LOGO} alt='Logo' className='nav-brand-logo' />
                )}
            </div>
            <div className={`side-nav-list flex flex-col${showCollapsedNav ? " side-nav-list--collapsed" : " gap-6"}`}>
                {navItems.map((data) => (
                    data.isGroup
                        ? renderGroup(data)
                        : renderNavItem(data)
                ))}
            </div>
            {renderCollapsedPanel()}
            {showCollapsedTooltips ? (
                <Tooltip id="collapsed-nav-tooltip" className="collapsed-nav-tooltip" />
            ) : null}

            <ConfirmModal
                isOpen={logoutModalOpen}
                onClose={() => setLogoutModalOpen(false)}
                onConfirm={handleLogout}
                title="Confirm Logout"
                content="Are you sure you want to logout?"
            />
        </div>
    );
}

export default SideNavbar
